import type React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

export interface LoadingStateProps {
  isLoading?: boolean;
  error?: any;
  loadingText?: string;
  errorTitle?: string;
  errorDescription?: string;
  onRetry?: () => void;
  retryText?: string;
  className?: string;
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  isLoading = false,
  error = null,
  loadingText = "Loading...",
  errorTitle = "Failed to load data",
  errorDescription = "There was an error loading the data. Please try again.",
  onRetry,
  retryText = "Retry",
  className = "border-primary/20"
}) => {
  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-0">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
              <p className="text-muted-foreground">{loadingText}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={className}>
        <CardContent className="p-0">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="h-12 w-12 bg-destructive/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                <AlertCircle className="h-6 w-6 text-destructive" />
              </div>
              <h3 className="text-lg font-medium mb-2 text-destructive">{errorTitle}</h3>
              <p className="text-muted-foreground mb-4">{errorDescription}</p>
              {onRetry && (
                <Button onClick={onRetry} variant="outline">
                  {retryText}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};
